/**
 * What we know, and — just as loudly — how we know it.
 *
 * ── Provenance has to be visible without being clutter ───────────────────────
 *
 * Twenty-five attributes each carry a provenance, which the server collapses
 * into one of six certainties. Printing a ten-word explanation beside every
 * value would bury the values; printing nothing would let "the seller says
 * it's a 2012" and "we photographed the VIN plate" look identical. So each
 * value wears a chip of two or three words, the chip's `title` carries the
 * sentence, and one legend at the bottom explains the whole vocabulary once.
 *
 * **An AI inference never looks like a confirmed fact.** `AI_INFERENCE` has
 * its own tone and its own dashed outline in `format.ts`, and it deliberately
 * does not collapse into OUR ESTIMATE — which is precisely what a rank-ordered
 * provenance would otherwise let it do.
 *
 * ── The unknown list is a feature ────────────────────────────────────────────
 *
 * `unknownFacts` is what the server could not answer. It is shown, not hidden:
 * an empty row and an absent row look identical, and only one of them is a
 * question somebody still has to ask a seller.
 */

import { useCallback } from 'react'
import { useLocale, type Locale } from '../../lib/i18n'
import { CERTAINTIES, asCertainty, conditionLabel, factLabel, provenanceLabel, structureLabel, titleStatusLabel } from '../../lib/domain'
import { certaintyBadge, dateText, feet, pounds, TONES } from '../../lib/format'
import { CertaintyChip, Chip, Empty, Line, Panel, S } from './ui'
import type { FactRow } from './types'

const EN = {
  title: 'What we know',
  none: 'Nothing has been recorded yet.',
  unknown: 'Still unknown',
  unknownWhy: 'Every one of these is a question for the seller or for an inspection.',
  legend: 'What the badges mean',
  yes: 'Yes',
  no: 'No',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Lo que sabemos',
  none: 'Todavía no se ha anotado nada.',
  unknown: 'Todavía sin saber',
  unknownWhy: 'Cada una de estas es una pregunta para el vendedor o para una inspección.',
  legend: 'Qué significan las etiquetas',
  yes: 'Sí',
  no: 'No',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

/**
 * A fact's value, in the units and vocabulary of its attribute.
 *
 * The server stores a text, a number or a boolean per attribute; rendering
 * `1` for "towable" or `28.5` for a length would be technically correct and
 * useless standing next to a trailer.
 */
function valueText(fact: FactRow, locale: Locale, t: (key: CopyKey) => string): string {
  const { attribute, value } = fact
  if (value === null) return '—'
  if (typeof value === 'boolean') return value ? t('yes') : t('no')

  switch (attribute) {
    case 'structure_type':
      return structureLabel(String(value), locale)
    case 'condition':
    case 'roof_condition':
      return conditionLabel(String(value), locale)
    case 'title_status':
      return titleStatusLabel(String(value), locale)
    case 'length_ft':
    case 'width_ft':
    case 'height_ft':
      return typeof value === 'number' ? feet(value, locale) : String(value)
    case 'weight_lbs':
      return typeof value === 'number' ? pounds(value, locale) : String(value)
    default:
      return fact.unit ? `${String(value)} ${fact.unit}` : String(value)
  }
}

export default function FactsPanel({ facts, unknown }: { facts: FactRow[]; unknown: string[] }) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])

  return (
    <Panel id="facts" title={t('title')} badge={<Chip tone={TONES.quiet}>{facts.length}</Chip>} defaultOpen>
      {facts.length === 0 ? (
        <Empty>{t('none')}</Empty>
      ) : (
        facts.map((fact, index) => (
          <Line
            key={fact.attribute}
            first={index === 0}
            label={factLabel(fact.attribute, locale)}
            after={<CertaintyChip certainty={asCertainty(fact.certainty)} locale={locale} />}
          >
            <span title={[fact.provenance ? provenanceLabel(fact.provenance, locale) : null, fact.capturedAt ? dateText(fact.capturedAt, locale) : null, fact.reference]
              .filter(Boolean)
              .join(' · ')}>
              {valueText(fact, locale, t)}
            </span>
          </Line>
        ))
      )}

      {unknown.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 4px' }}>
            {t('unknown')} ({unknown.length})
          </h3>
          <p style={S.note}>{t('unknownWhy')}</p>
          <div style={{ ...S.chipRow, marginTop: 8 }}>
            {unknown.map((attribute) => (
              <Chip key={attribute} tone={TONES.quiet} outlined>
                {factLabel(attribute, locale)}
              </Chip>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>{t('legend')}</h3>
        {CERTAINTIES.map((certainty) => {
          const badge = certaintyBadge(certainty, locale)
          return (
            <div key={certainty} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
              <span style={{ flex: '0 0 auto' }}>
                <CertaintyChip certainty={certainty} locale={locale} />
              </span>
              <span style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.4 }}>{badge.hint}</span>
            </div>
          )
        })}
      </div>
    </Panel>
  )
}
